/**
When on PDP (caveat: no quantity selector on the PDP)
1. A line item value is generated when the product form component is built
2. When user clicks gift wrap plus button, an `input type=hidden` is created, with name = 'properties[_Gift wrapping]' and `value=value from step 1`
2b. If the user clicks the `remove gift wrap` button, the hidden input is deleted
3. When the user clicks add to cart, if the product form has the correct property (ie, the gift wrapping property), the gift wrap product is manually added to cart, with a line item property that matches the original product

When on a product card: no option to add gift wrap

From cart/mini cart line item
1. handle checkbox
a) should add/remove the gift wrap product
b) should add/remove gift wrap line item
2. handle quantity selector 
a) should add/remove the gift wrap product
b) should add/remove gift wrap line item
3. handle remove button 
a) should remove corresponding gift wrap product as well

 */

import { utils, LitElement } from '@hh/global.js'
const {
  changeLineItem,
  reRenderShopifySections,
  fetchCartHTML,
  updateQuantity,
} = utils

class HHGiftWrap extends LitElement {
  static properties = {
    // id of main product
    productId: {
      type: Number,
    },
    // index of line item
    lineID: {
      type: String,
    },
    // whether the current product *is* gift wrap
    isGiftWrap: {
      type: Boolean,
      attribute: 'is-gift-wrap',
    },
    // whether the current product *is* a freebie
    isFreebie: {
      type: Boolean,
      attribute: 'is-freebie',
    },
    // whether the current product *already* has gift wrap connected
    hasGiftWrap: {
      type: Boolean,
      reflect: true,
      attribute: 'has-gift-wrap',
    },
    // line item gift wrap property value (timestamp)
    lineItemValue: {
      type: Number,
    },
    // line item quantity
    quantity: {
      type: Number,
    },
    lineItemPropertiesEncoded: {
      type: String,
    },
    lineItemEntityDescriptionKey: {
      type: String,
    },
    lineItemEntityDescriptionValue: {
      type: String,
    },
  }

  constructor() {
    super()

    this.dateStamp = Date.now()
    this.inlineCartSectionId =
      document.getElementById('inline-cart')?.dataset.id || 'inline-cart'
  }

  connectedCallback() {
    this.addEventListener(
      'handleGiftWrapFromCart',
      this.handleGiftWrapFromCart.bind(this)
    )

    const LIPJSONString = this.querySelector(
      '[data-line-item-properties]'
    ).textContent
    this.lineItemProperties = JSON.parse(LIPJSONString) ?? {}

    if (
      this.getAttribute('gift-wrap-revalidate') === 'true' ||
      this.getAttribute('fixed-bundle-revalidate') === 'true'
    ) {
      const cartComponent =
        this.closest('hh-mini-cart') || this.closest('hh-inline-cart')
      cartComponent?.debouncedRevalidate()
    }

    // if the product is a freebie and the quantity is greater than 1, validate the quantity to 1
    if (this.isFreebie && this.quantity > 1) {
      this.validateFreebieQty()
    }
  }

  disconnectedCallback() {
    this.removeEventListener(
      'handleGiftWrapFromCart',
      this.handleGiftWrapFromCart
    )
  }

  async validateFreebieQty() {
    reRenderShopifySections(await updateQuantity(this.lineID, 1))
  }

  // when the checkbox in the cart line item is toggled
  async handleGiftWrapFromCart(e) {
    const isChecked = e.detail.value

    if (isChecked) {
      this.addGiftWrap(e.detail.lineID)
    } else {
      this.removeGiftWrap(e.detail.lineItemValue)
    }
  }

  async addGiftWrap(lineId) {
    if (!lineId) {
      return
    }

    let sections = {}

    this.closest('hh-mini-cart')?.setAttribute('loading', true)
    this.closest('hh-inline-cart')?.setAttribute('loading', true)

    try {
      const updateCartResponse = await this.addLIP(lineId)
      if (updateCartResponse) {
        sections = updateCartResponse.sections
        reRenderShopifySections(updateCartResponse)
        document.dispatchEvent(new CustomEvent('recommendations:refresh'))
      }
    } catch (e) {
      // Refetch cart due to stale data
      const updatedCartHTMLResponse = await fetchCartHTML()
      if (updatedCartHTMLResponse) {
        sections = updatedCartHTMLResponse
        reRenderShopifySections({ sections: updatedCartHTMLResponse })
        document.dispatchEvent(new CustomEvent('recommendations:refresh'))
      }
    } finally {
      document.querySelector('hh-mini-cart')?.removeAttribute('loading')
      document.querySelector('hh-inline-cart')?.removeAttribute('loading')
    }

    return sections
  }

  async removeGiftWrap(targetLineItemValue) {
    if (!targetLineItemValue) {
      return
    }

    this.closest('hh-mini-cart')?.setAttribute('loading', true)
    this.closest('hh-inline-cart')?.setAttribute('loading', true)

    try {
      const updateCartResponse = await this.removeLIP(targetLineItemValue)
      if (updateCartResponse) {
        reRenderShopifySections(updateCartResponse)
        document.dispatchEvent(new CustomEvent('recommendations:refresh'))
      }
    } catch (e) {
      // Refetch cart due to stale data
      const updatedCartHTMLResponse = await fetchCartHTML()
      if (updatedCartHTMLResponse) {
        reRenderShopifySections({ sections: updatedCartHTMLResponse })
        document.dispatchEvent(new CustomEvent('recommendations:refresh'))
      }
    } finally {
      document.querySelector('hh-mini-cart')?.removeAttribute('loading')
      document.querySelector('hh-inline-cart')?.removeAttribute('loading')
    }
  }

  // add line item property to the product (ie, the one that's getting gift wrapped)
  async addLIP(line_id) {
    const properties = {
      ...this.lineItemProperties,
      '_Gift wrapping': `${this.dateStamp}`,
      [this.lineItemEntityDescriptionKey]: this.lineItemEntityDescriptionValue,
      '_gift-wrap-key': this.lineItemEntityDescriptionKey,
      '_gift-wrap-value': this.lineItemEntityDescriptionValue,
      _gift_wrap_items: `${this.getAttribute('gift-wrap-items')}`,
      _gift_wrap_items_hmac: `${this.getAttribute('gift-wrap-hmac')}`,
    }

    delete properties['_gift_wrap_removed']

    const body = {
      id: line_id,
      quantity: this.quantity,
      properties,
    }

    return await changeLineItem(
      body,
      `${this.inlineCartSectionId},header,mini-cart`
    )
  }

  async removeLIP(target_line_item_value) {
    const target_product = document.querySelector(
      `hh-gift-wrap[has-gift-wrap][lineItemValue='${target_line_item_value}']`
    ).lineID
    const body = {
      id: target_product,
      quantity: this.quantity,
      properties: {
        ...this.lineItemProperties,
        _gift_wrap_removed: 'true',
      },
    }

    delete body.properties['_gift-wrap-key']
    delete body.properties['_gift-wrap-value']
    delete body.properties['_Gift wrapping']
    delete body.properties[this.lineItemEntityDescriptionKey]
    delete body.properties['_gift_wrap_items']
    delete body.properties['_gift_wrap_items_hmac']

    return await changeLineItem(
      body,
      `${this.inlineCartSectionId},header,mini-cart`
    )
  }

  async setFixedBundleLIP() {
    this.closest('hh-mini-cart')?.setAttribute('loading', true)
    this.closest('hh-inline-cart')?.setAttribute('loading', true)

    try {
      const properties = {
        ...this.lineItemProperties,
        _bundled_variants: `${this.getAttribute('fixed-bundle-variants')}`,
        _bundled_variants_hmac: `${this.getAttribute('fixed-bundle-hmac')}`,
      }

      const body = {
        id: this.lineID,
        quantity: this.quantity,
        properties,
      }

      const updateCartResponse = await changeLineItem(
        body,
        `${this.inlineCartSectionId},header,mini-cart`
      )

      if (updateCartResponse) {
        reRenderShopifySections(updateCartResponse)
        document.dispatchEvent(new CustomEvent('recommendations:refresh'))
      }

      return updateCartResponse.sections
    } catch (e) {
      console.error('Failed to set fixed bundle LIP', e)
    } finally {
      document.querySelector('hh-mini-cart')?.removeAttribute('loading')
      document.querySelector('hh-inline-cart')?.removeAttribute('loading')
    }
  }
}

customElements.define('hh-gift-wrap', HHGiftWrap)
